import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Inline-SVG country flags. Ported from voicebot-portal
 * (`src/client/components/ui/flag-icon.tsx`) so they render identically on
 * Windows — emoji flags strip to letters on Segoe UI Emoji. Unknown ISO-2
 * codes render as the text code in a muted chip.
 *
 * Covers the brand/language corpus on tsrv: DE, GB, NL, FR, IT, ES, CH, JP,
 * TH, AT. Add more entries here on demand — each flag is a hand-drawn SVG
 * kept as short as possible.
 */
const FLAGS: Record<string, string> = {
  DE: `<svg viewBox="0 0 640 480" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 0h640v160H0z" fill="#000"/>
    <path d="M0 160h640v160H0z" fill="#D00"/>
    <path d="M0 320h640v160H0z" fill="#FFCE00"/>
  </svg>`,
  GB: `<svg viewBox="0 0 640 480" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 0h640v480H0z" fill="#012169"/>
    <path d="m75 0 244 181L562 0h78v62L400 241l240 178v61h-80L320 302 81 480H0v-60l239-178L0 64V0z" fill="#FFF"/>
    <path d="m424 281 216 159v40L369 281zm-184 20 6 35L54 480H0zM640 0v3L391 191l2-44L590 0zM0 0l239 176h-60L0 42z" fill="#C8102E"/>
    <path d="M241 0v480h160V0zM0 160v160h640V160z" fill="#FFF"/>
    <path d="M0 193v96h640v-96zM273 0v480h96V0z" fill="#C8102E"/>
  </svg>`,
  NL: `<svg viewBox="0 0 640 480" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 0h640v480H0z" fill="#21468B"/>
    <path d="M0 0h640v320H0z" fill="#FFF"/>
    <path d="M0 0h640v160H0z" fill="#AE1C28"/>
  </svg>`,
  FR: `<svg viewBox="0 0 640 480" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 0h213.3v480H0z" fill="#002654"/>
    <path d="M213.3 0h213.4v480H213.3z" fill="#FFF"/>
    <path d="M426.7 0H640v480H426.7z" fill="#CE1126"/>
  </svg>`,
  IT: `<svg viewBox="0 0 640 480" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 0h213.3v480H0z" fill="#009246"/>
    <path d="M213.3 0h213.4v480H213.3z" fill="#FFF"/>
    <path d="M426.7 0H640v480H426.7z" fill="#CE2B37"/>
  </svg>`,
  ES: `<svg viewBox="0 0 640 480" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 0h640v480H0z" fill="#AA151B"/>
    <path d="M0 120h640v240H0z" fill="#F1BF00"/>
  </svg>`,
  CH: `<svg viewBox="0 0 640 480" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 0h640v480H0z" fill="#D52B1E"/>
    <path d="M170 195h300v90H170z" fill="#FFF"/>
    <path d="M275 90h90v300h-90z" fill="#FFF"/>
  </svg>`,
  JP: `<svg viewBox="0 0 640 480" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 0h640v480H0z" fill="#FFF"/>
    <circle cx="320" cy="240" fill="#BC002D" r="120"/>
  </svg>`,
  TH: `<svg viewBox="0 0 640 480" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 0h640v480H0z" fill="#ED1C24"/>
    <path d="M0 80h640v320H0z" fill="#FFF"/>
    <path d="M0 160h640v160H0z" fill="#241D4F"/>
  </svg>`,
  AT: `<svg viewBox="0 0 640 480" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 0h640v480H0z" fill="#FFF"/>
    <path d="M0 0h640v160H0z" fill="#C8102E"/>
    <path d="M0 320h640v160H0z" fill="#C8102E"/>
  </svg>`,
};

@Component({
  selector: 'cmt-flag',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (svg(); as s) {
      <span
        class="inline-block overflow-hidden rounded-[2px] border border-border/60 shrink-0 align-[-2px]"
        [style.width]="width()"
        [style.height]="height()"
        [attr.aria-label]="iso() ?? 'flag'"
        [innerHTML]="s"
      ></span>
    } @else if (iso()) {
      <span
        class="inline-flex items-center justify-center rounded-[2px] bg-muted text-[0.6rem] font-mono font-semibold text-muted-foreground shrink-0 align-[-2px]"
        [style.width]="width()"
        [style.height]="height()"
      >{{ iso() }}</span>
    }
  `,
})
export class CmtFlagComponent {
  /** ISO-3166 alpha-2 code. Case-insensitive. */
  readonly iso = input<string | null | undefined>(null);
  readonly width = input('1.125rem');
  readonly height = input('0.8rem');

  private readonly sanitizer = inject(DomSanitizer);

  protected readonly svg = computed<SafeHtml | null>(() => {
    const code = this.iso()?.toUpperCase();
    if (!code) return null;
    const raw = FLAGS[code];
    if (!raw) return null;
    // Source is hand-authored constants — safe to bypass.
    return this.sanitizer.bypassSecurityTrustHtml(raw);
  });
}
